import clsx from 'clsx';
import { type ReactNode } from 'react';
import { forwardRef } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  expandableTextCSS,
  expandableTextButtonCSS,
} from './ExpandableText.sty.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useId } from '../../core/hooks/useId.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';

const COMPONENT_NAME = 'ExpandableText';

// #region Typings

/**
 * @public
 */
export interface ExpandableTextBaseProps
  extends WithChildren,
    StylingProps,
    DataTestId,
    MaskingProps,
    AriaLabelingProps,
    BehaviorTrackingProps {
  /**
   * Text shown for expanding the content.
   * @defaultValue 'Show more'
   */
  expandLabel?: ReactNode;
  /**
   * Text shown for collapsing the content.
   * @defaultValue 'Show less'
   */
  collapseLabel?: ReactNode;
}

/**
 * @public
 */
export interface ExpandableTextUncontrolledProps {
  /**
   * Determines whether or not the content is initially expanded in an uncontrolled scenario.
   * @defaultValue false
   */
  defaultExpanded?: boolean;
  /**
   * Determines whether or not the content is expanded in a controlled scenario.
   */
  expanded?: never;
  /**
   * Callback fired when the expanded attribute changes in a controlled scenario.
   */
  onExpandChange?: never;
}

/**
 * @public
 */
export interface ExpandableTextControlledProps {
  /**
   * Determines whether or not the content is initially expanded in an uncontrolled scenario.
   */
  defaultExpanded?: never;
  /**
   * Determines whether or not the content is expanded in a controlled scenario.
   */
  expanded: boolean;
  /**
   * Callback fired when the expanded attribute changes in a controlled scenario.
   */
  onExpandChange: (expanded: boolean) => void;
}

/**
 * Accepted properties for ExpandableText
 * @public
 */
export type ExpandableTextProps = ExpandableTextBaseProps &
  (ExpandableTextControlledProps | ExpandableTextUncontrolledProps);

/**
 * The `ExpandableText` component provides expand and collapse functionality for inline text.
 * @public
 */
export const ExpandableText = forwardRef<HTMLElement, ExpandableTextProps>(
  (props, forwardedRef) => {
    const {
      children,
      expandLabel = (
        <FormattedMessage
          description="Shown for expandable text."
          defaultMessage="Show more"
          id="DV3d5lBJda+EHJja"
        />
      ),
      collapseLabel = (
        <FormattedMessage
          description="Shown for collapsible text."
          defaultMessage="Show less"
          id="986buSbL34GVFlBf"
        />
      ),
      defaultExpanded = false,
      expanded,
      onExpandChange,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    } = props;

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);
    const ariaLabelingProps = useAriaLabelingProps(restProps);
    const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

    const [isExpanded, setExpanded] = useControllableState({
      defaultValue: defaultExpanded,
      value: expanded,
      onChange: onExpandChange,
    });

    const textId = useId();
    const showMoreLessDataDtProperties = `state:${
      isExpanded ? 'less' : 'more'
    }`;

    // note: need to check for more than just space character
    const beginsWithWhitespace =
      Array.isArray(children) === false &&
      typeof children === 'string' &&
      /^\s/.test(children);

    return (
      <>
        <span
          ref={forwardedRef}
          id={textId}
          role="region"
          className={clsx(
            expandableTextCSS({ expanded: isExpanded }),
            consumerClassName,
          )}
          style={consumerStyle}
          data-testid={dataTestId}
          data-dtrum-mask={dataDtrumMask}
          data-dtrum-allow={dataDtrumAllow}
          {...ariaLabelingProps}
          {...behavioralTrackingProps}
          {...forwardProps}
          data-dt-component={COMPONENT_NAME}
        >
          {isExpanded && beginsWithWhitespace && ' '}
          {isExpanded && children}
        </span>{' '}
        <button
          onClick={() => setExpanded(!isExpanded)}
          aria-controls={textId}
          aria-expanded={isExpanded}
          className={expandableTextButtonCSS}
          data-dtrum-mask={dataDtrumMask}
          data-dtrum-allow={dataDtrumAllow}
          data-dt-name="ShowMoreLess"
          data-dt-properties={showMoreLessDataDtProperties}
        >
          {isExpanded ? collapseLabel : expandLabel}
        </button>
      </>
    );
  },
);

(
  ExpandableText as typeof ExpandableText & { displayName: string }
).displayName = COMPONENT_NAME;
